import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, ArrowRight, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatINR } from "@/context/CartContext";
import { Reveal, Kicker } from "@/components/Motion";
import ScrollCarousel from "@/components/ScrollCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add, buyNow } = useCart();

  const [data, setData] = useState({ products: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setImgIdx(0); }, [id]);

  const product = useMemo(() => data.products.find(p => p.id === id), [data.products, id]);
  const category = useMemo(
    () => product ? data.categories.find(c => c.id === product.category) : null,
    [data.categories, product]
  );

  const subcategory = useMemo(
    () => product?.subcategory ? category?.subcategories?.find(s => s.id === product.subcategory) || null : null,
    [category, product]
  );

  useEffect(() => {
    if (!product) return;
    setVariant(product.variants?.[0]?.label ?? null);
  }, [product]);

  const chosenVariant = product?.variants?.find(v => v.label === variant) || null;

  const images = useMemo(() => {
    if (!product) return [];
    const base = product.images || [];
    const primary = chosenVariant?.images?.[0];
    if (primary) return [primary, ...base.filter(x => x !== primary)];
    return base;
  }, [product, chosenVariant]);

  const sp = chosenVariant?.sp ?? product?.sp ?? 0;
  const mrp = chosenVariant?.mrp ?? product?.mrp ?? 0;
  const offer = product?.offer;
  const finalSp = offer ? Math.round(sp * (100 - offer.discount_percent) / 100) : sp;
  const save = mrp > sp ? Math.round(((mrp - sp) / mrp) * 100) : 0;
  const desc = chosenVariant?.desc || product?.long_desc || product?.desc;

  const related = useMemo(() => {
    if (!product) return [];
    const match = (p) => product.subcategory ? p.subcategory === product.subcategory : p.category === product.category;
    return data.products.filter(p => match(p) && p.id !== product.id);
  }, [data.products, product]);

  if (loading) return <div className="pt-40 text-center text-[#5c3e2b]/60 font-display text-2xl">Loading…</div>;
  if (!product) return (
    <div className="pt-40 pb-20 text-center">
      <p className="font-display text-3xl">Product not found.</p>
      <Link to="/shop" className="inline-block mt-6 border border-[#2b2320] rounded-full px-8 py-3 text-sm">Back to Shop</Link>
    </div>
  );

  const doAdd = () => { add(product, { variant, qty: 1 }); };
  const doBuyNow = () => { buyNow(product, { variant, qty: 1 }); toast.success("Redirecting to checkout"); };

  const prev = () => setImgIdx((imgIdx - 1 + images.length) % images.length);
  const next = () => setImgIdx((imgIdx + 1) % images.length);

  return (
    <div data-testid="product-detail-page" className="pt-32 md:pt-36 pb-24">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Breadcrumb */}
        <nav className="text-xs tracking-[0.16em] uppercase text-[#5c3e2b]/80 flex gap-2 items-center mb-8">
          <Link to="/" className="hover:text-[#2b2320]">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#2b2320]">Shop</Link>
          {category && (<><span>/</span><span className="text-[#2b2320]">{category.title}</span></>)}
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16">
          {/* --------- Gallery --------- */}
          <div>
            <div className="relative overflow-hidden rounded-sm bg-[#ece3d4] aspect-[4/5]">
              <img
                key={images[imgIdx]}
                src={images[imgIdx]}
                alt={product.name}
                className="h-full w-full object-cover"
              />
              {images.length > 1 && (
                <>
                  <button data-testid="pdp-prev" onClick={prev} aria-label="Previous image"
                    className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#f8f6f2]/90 text-[#2b2320] flex items-center justify-center hover:bg-[#f8f6f2]">
                    <ChevronLeft size={18} />
                  </button>
                  <button data-testid="pdp-next" onClick={next} aria-label="Next image"
                    className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-[#f8f6f2]/90 text-[#2b2320] flex items-center justify-center hover:bg-[#f8f6f2]">
                    <ChevronRight size={18} />
                  </button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3">
                {images.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setImgIdx(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`h-20 w-20 rounded-sm overflow-hidden bg-[#ece3d4] transition-opacity ${
                      i === imgIdx ? "opacity-100 ring-1 ring-[#2b2320]" : "opacity-60 hover:opacity-100"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* --------- Details --------- */}
          <div>
            <Kicker>{subcategory?.title || category?.title || product.collection}</Kicker>
            <h1 data-testid="pdp-name" className="font-display fs-product_title tracking-tight mt-4 leading-none">{product.name}</h1>

            {product.fragrances?.length > 0 && (
              <p className="text-sm text-[#5c3e2b] mt-4 tracking-wide">{product.fragrances.join(" · ")}</p>
            )}

            <div className="flex items-baseline gap-4 mt-7">
              <span data-testid="pdp-sp" className="font-display fs-product_price">{formatINR(offer ? finalSp : sp)}</span>
              {offer ? (
                <>
                  <span className="text-xl text-[#2b2320]/40 line-through">{formatINR(sp)}</span>
                  <span className="text-xs tracking-[0.12em] uppercase bg-[#395439] text-[#f8f6f2] rounded-full px-3 py-1">
                    {offer.name} · {offer.discount_percent}% off
                  </span>
                </>
              ) : mrp > sp ? (
                <>
                  <span className="text-xl text-[#2b2320]/40 line-through">{formatINR(mrp)}</span>
                  <span className="text-xs tracking-[0.12em] uppercase text-[#395439]">Save {save}%</span>
                </>
              ) : null}
            </div>

            {desc && (
              <p className="fs-product_description text-[#2b2320]/75 mt-6 leading-relaxed">{desc}</p>
            )}

            {/* Variants */}
            {product.variants?.length > 0 && (
              <div className="mt-7">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#5c3e2b] mb-2">Variant</p>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map(v => (
                    <button
                      key={v.label}
                      onClick={() => setVariant(v.label)}
                      data-testid={`pdp-variant-${v.label.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}
                      className={`text-sm px-4 py-2 rounded-full border transition-colors duration-300 ${
                        variant === v.label ? "bg-[#2b2320] text-[#f8f6f2] border-[#2b2320]" : "border-[#2b2320]/25 text-[#2b2320]/75 hover:border-[#2b2320]"
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-9">
              <button
                onClick={doAdd}
                data-testid="pdp-add-to-cart"
                className="group inline-flex items-center justify-center gap-2 bg-[#2b2320] text-[#f8f6f2] rounded-full px-8 py-4 text-sm tracking-wide hover:bg-[#395439] transition-colors duration-300 flex-1"
              >
                Add to Cart <Plus size={15} className="transition-transform duration-300 group-hover:rotate-90" />
              </button>
              <button
                onClick={doBuyNow}
                data-testid="pdp-buy-now"
                className="group inline-flex items-center justify-center gap-2 border border-[#2b2320] text-[#2b2320] rounded-full px-8 py-4 text-sm tracking-wide hover:bg-[#2b2320] hover:text-[#f8f6f2] transition-colors duration-300 flex-1"
              >
                Buy Now <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>

            {/* Includes */}
            {product.includes?.length > 0 && (
              <div className="mt-10 border-t border-[#2b2320]/10 pt-7">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#5c3e2b] mb-3">What's inside</p>
                <ul className="space-y-2">
                  {product.includes.map((line, i) => (
                    <li key={i} className="flex gap-3 text-sm text-[#2b2320]/80">
                      <Check size={16} className="mt-0.5 text-[#395439] shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Ship promise */}
            <div className="mt-8 text-xs text-[#5c3e2b]/85 tracking-wide">
              ₹100 flat shipping within India. Free above ₹2,000.
            </div>
          </div>
        </div>

        {/* --------- Ritual --------- */}
        {product.ritual && (
          <section data-testid="pdp-ritual" className="mt-24 md:mt-32 bg-[#2b2320] text-[#f8f6f2] rounded-sm px-6 md:px-16 py-16 md:py-20">
            <Reveal>
              <Kicker className="text-[#e6b980]">The Ritual</Kicker>
              <h2 className="font-display fs-ritual_title mt-4 leading-tight max-w-2xl">{product.ritual.title}</h2>
            </Reveal>
            <ol className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8 max-w-4xl">
              {product.ritual.steps.map((step, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-5">
                    <span className="font-display text-3xl text-[#e6b980] shrink-0 leading-none">{String(i + 1).padStart(2, "0")}</span>
                    <p className="fs-ritual_step leading-relaxed text-[#f8f6f2]/85">{step}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </section>
        )}

        {/* --------- Related --------- */}
        {related.length > 0 && (
          <section className="mt-24 md:mt-32">
            <Reveal><Kicker>You may also like</Kicker></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-3">More from {subcategory?.title || category?.title}</h2>
            </Reveal>
            <ScrollCarousel
              className="mt-10"
              resetKey={id}
              prevLabel="Scroll related products left"
              nextLabel="Scroll related products right"
              prevTestId="related-prev"
              nextTestId="related-next"
              trackClassName="gap-6 md:gap-8"
            >
              {related.map(r => (
                <Link
                  key={r.id}
                  to={`/product/${r.id}`}
                  className="group block shrink-0 snap-start w-[calc(50%-0.75rem)] md:w-[calc(50%-1rem)] lg:w-[calc(25%-1.5rem)]"
                >
                  <div className="aspect-[4/5] overflow-hidden rounded-sm bg-[#ece3d4]">
                    <img src={r.images?.[0]} alt={r.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="mt-3 flex items-baseline justify-between gap-3">
                    <p className="font-display fs-related_name leading-tight">{r.name}</p>
                    <p className="fs-product_card_price shrink-0">{formatINR(r.sp)}</p>
                  </div>
                </Link>
              ))}
            </ScrollCarousel>
          </section>
        )}
      </div>
    </div>
  );
}
